/**
 * Configuration Service (Signup)
 * Simplified config service for the signup/onboarding app
 */

import { BackendConfig, DEFAULT_BACKEND_CONFIG } from '../types/config';
import { getRuntimeConfig } from '../config/runtime';

const CONFIG_CACHE_KEY = 'app_config_cache';
const CONFIG_CACHE_DURATION = 1000 * 60 * 60; // 1 hour
const CONFIG_CACHE_VERSION = 4;

let currentConfig: BackendConfig | null = null;
let configPromise: Promise<BackendConfig> | null = null;

interface CachedConfig {
  version: number;
  data: BackendConfig;
  timestamp: number;
}

function isCacheValid(cached: CachedConfig): boolean {
  if (cached.version !== CONFIG_CACHE_VERSION) return false;
  const age = Date.now() - cached.timestamp;
  return age < CONFIG_CACHE_DURATION;
}

function getCachedConfig(): BackendConfig | null {
  try {
    const cached = localStorage.getItem(CONFIG_CACHE_KEY);
    if (!cached) return null;
    const parsed: CachedConfig = JSON.parse(cached);
    if (isCacheValid(parsed)) return parsed.data;
    localStorage.removeItem(CONFIG_CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

function setCachedConfig(config: BackendConfig): void {
  try {
    const cached: CachedConfig = {
      version: CONFIG_CACHE_VERSION,
      data: config,
      timestamp: Date.now(),
    };
    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(cached));
  } catch (error) {
  }
}

/**
 * Load configuration
 */
export async function loadConfig(): Promise<BackendConfig> {
  if (configPromise) return configPromise;
  if (currentConfig) return currentConfig;

  configPromise = (async () => {
    const cached = getCachedConfig();
    if (cached) {
      currentConfig = cached;
      return cached;
    }

    // Signup app uses default config — no WebSocket needed
    currentConfig = DEFAULT_BACKEND_CONFIG;
    configPromise = null;
    return DEFAULT_BACKEND_CONFIG;
  })();

  return configPromise;
}

/**
 * Get current loaded config (synchronous)
 */
export function getConfig(): BackendConfig | null {
  return currentConfig;
}

/**
 * Apply theme colors to document
 */
export function applyThemeColors(config: BackendConfig): void {
  const root = document.documentElement;
  const theme = config.theme;

  root.style.setProperty('--primary-color', theme.primaryColor);
  root.style.setProperty('--secondary-color', theme.secondaryColor);
  root.style.setProperty('--accent-color', theme.accentColor);
  root.style.setProperty('--bg-color', theme.backgroundColor);
  root.style.setProperty('--text-color', theme.textColor);
  root.style.setProperty('--border-color', theme.borderColor);
  root.style.setProperty('--success-color', theme.successColor);
  root.style.setProperty('--warning-color', theme.warningColor);
  root.style.setProperty('--error-color', theme.errorColor);

  const gradient = `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%)`;
  root.style.setProperty('--gradient-primary', gradient);

  if (theme.fontFamily) {
    root.style.setProperty('--font-family', theme.fontFamily);
  }

  if (config.companyLogo) {
    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (favicon) favicon.href = config.companyLogo;
  }

  document.title = config.appName;

  if (config.ui.enableDarkMode) {
    document.documentElement.classList.add('dark-mode');
  } else {
    document.documentElement.classList.remove('dark-mode');
  }

}
